import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Card, EmptyState, ErrorState, PageHeader, Pill, Skeleton } from "../components/ui";
import { createScan, deleteScan, fetchScans, reprocessScan } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import type { PantryScanSubmission } from "../types";

function humanDate(value?: string): string {
  if (!value) {
    return "Unknown";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function scanTitle(scan: PantryScanSubmission): string {
  if (scan.title && scan.title.trim()) {
    return scan.title;
  }
  return "Pantry scan";
}

export function ScansPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scans, setScans] = useState<PantryScanSubmission[]>([]);
  const [creating, setCreating] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchScans();
      setScans(Array.isArray(payload) ? payload : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load scans.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onFilesSelected(files: FileList | null): Promise<void> {
    if (!files || files.length === 0) {
      return;
    }
    const selected = Array.from(files).slice(0, 6);
    const totalBytes = selected.reduce((sum, file) => sum + file.size, 0);

    setCreating(true);
    setError(null);
    trackEvent("scan_upload_started", {
      file_count: selected.length,
      total_size_kb: Math.round(totalBytes / 1024),
    });
    try {
      const form = new FormData();
      form.append("mode", "scan_food");
      selected.forEach((file) => {
        form.append("images", file, file.name);
      });
      const created = await createScan(form);
      setScans((current) => [created, ...current.filter((scan) => scan.id !== created.id)]);
      trackEvent("scan_created", { file_count: selected.length });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to create scan.");
      trackEvent("scan_create_failed", { file_count: selected.length });
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(scanId: string): Promise<void> {
    const confirmed = window.confirm("Delete this scan?");
    if (!confirmed) {
      return;
    }
    setPendingId(scanId);
    try {
      await deleteScan(scanId);
      setScans((current) => current.filter((scan) => scan.id !== scanId));
      trackEvent("scan_deleted", { source: "scans_list" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to delete scan.");
    } finally {
      setPendingId(null);
    }
  }

  async function handleReprocess(scanId: string): Promise<void> {
    setPendingId(scanId);
    try {
      const updated = await reprocessScan(scanId);
      setScans((current) => current.map((scan) => (scan.id === scanId ? updated : scan)));
      trackEvent("scan_reprocessed", { source: "scans_list" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to reprocess scan.");
    } finally {
      setPendingId(null);
    }
  }

  const grouped = useMemo(() => {
    return [...scans].sort((a, b) => {
      const left = new Date(a.created_at ?? "").getTime();
      const right = new Date(b.created_at ?? "").getTime();
      return right - left;
    });
  }, [scans]);

  return (
    <div data-testid="scans-page" className="space-y-4 pb-4">
      <PageHeader title="Scans" subtitle="Upload pantry photos and manage scan history." />

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Create a new pantry scan</p>
            <p className="text-sm text-[var(--pr-text-muted)]">Upload up to 6 shelf photos in one submission.</p>
          </div>
          <label className="pr-btn-primary inline-flex min-h-10 cursor-pointer items-center px-4 text-sm font-semibold">
            {creating ? "Uploading..." : "Upload photos"}
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={(event) => {
                void onFilesSelected(event.target.files);
                event.currentTarget.value = "";
              }}
              disabled={creating}
            />
          </label>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
          <Skeleton className="h-24" />
        </div>
      ) : error ? (
        <ErrorState message={error} onRetry={() => void load()} />
      ) : grouped.length === 0 ? (
        <EmptyState
          title="No scans yet"
          subtitle="Start with your first scan to build pantry intelligence."
          image={assetUrl("pantry-inventory-bg.png")}
        />
      ) : (
        <div className="space-y-3">
          {grouped.map((scan) => {
            const isPending = pendingId === scan.id;
            const cover = scan.images?.[0]?.presigned_url ?? scan.images?.[0]?.url ?? assetUrl("pantry-inventory-bg.png");
            return (
              <Card key={scan.id} flat className="overflow-hidden">
                <div className="relative mb-3 h-28 overflow-hidden rounded-xl border border-[var(--pr-border)] bg-[var(--pr-surface-secondary)]">
                  <img src={cover} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/10 to-black/15" />
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate text-base font-semibold">{scanTitle(scan)}</h3>
                      <Pill tone="accent" label={(scan.status || "unknown").toUpperCase()} />
                    </div>
                    <p className="mt-1 text-sm text-[var(--pr-text-muted)]">Uploaded {humanDate(scan.created_at)}</p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Link
                      to={`/app/scans/${scan.id}`}
                      className="pr-btn-secondary inline-flex min-h-10 items-center px-3 text-sm font-semibold"
                    >
                      View details
                    </Link>
                    <Button
                      variant="secondary"
                      className="min-h-10 px-3 text-sm"
                      onClick={() => void handleReprocess(scan.id)}
                      disabled={isPending}
                    >
                      Reprocess
                    </Button>
                    <Button
                      variant="ghost"
                      className="min-h-10 border border-[var(--pr-border)] px-3 text-sm text-rose-600"
                      onClick={() => void handleDelete(scan.id)}
                      disabled={isPending}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
